namespace SolovisGateway.Admin.Organizations {

    export const GetConfigureLinkOutcome = {
        Generated: "Generated",
        ServedExisting: "ServedExisting",
    } as const;
    export type GetConfigureLinkOutcome = typeof GetConfigureLinkOutcome[keyof typeof GetConfigureLinkOutcome];

    export interface OrgConfigureConnectionLinkDialogOptions {
        setupLink: string;
        connectionStatus?: string;
        // Generated when this is a freshly issued link (the normal case). ServedExisting when a
        // prior link was still potentially live but revoking it failed, so this is that same link
        // handed back rather than a new one — see GetConfigureLinkAsync. Treated as Generated when
        // omitted, so existing callers/tests keep working.
        outcome?: GetConfigureLinkOutcome;
        onClose?: () => void;
    }

    export class OrgConfigureConnectionLinkDialog {

        private _options: OrgConfigureConnectionLinkDialogOptions;

        constructor(options: OrgConfigureConnectionLinkDialogOptions) {
            this._options = options;
        }

        public show() {
            var self = this;
            var dialog = null;

            var isUpdate = this._options.connectionStatus === SsoConnectionStatus.Enabled || this._options.connectionStatus === SsoConnectionStatus.Disabled;

            var body = $("<div>").css({ "padding": "0 10px" });

            // Intro text
            body.append($("<p>").text(
                isUpdate
                    ? "Open this link to update the connection, or share it with the organization’s IT support team. Whoever opens it needs access to the organization’s identity provider."
                    : "Open this link to set up the connection, or share it with the organization’s IT support team. Whoever opens it needs access to the organization’s identity provider."
            ));

            // ServedExisting: the previous link couldn't be confirmed invalidated, so this is that
            // same link again, not a new one — say so rather than let it look like nothing happened.
            if (this._options.outcome === GetConfigureLinkOutcome.ServedExisting) {
                var servedExistingBanner = $("<div>").css({
                    "background-color": "#f2dede",
                    "border": "1px solid #ebccd1",
                    "color": "#a94442",
                    "padding": "12px 15px",
                    "border-radius": "4px",
                    "margin-bottom": "15px"
                });
                servedExistingBanner.text(
                    "We couldn't confirm the previous link was invalidated, so this is that same link " +
                    "again rather than a new one, to avoid two working links existing at once. If it has " +
                    "already expired or been used, try Configure again in a moment."
                );
                body.append(servedExistingBanner);
            }

            // Setup/Update Link box
            var linkBox = $("<div>").css({
                "border": "1px solid #ddd",
                "padding": "15px",
                "margin-bottom": "15px"
            });
            linkBox.append($("<div>").css({ "font-weight": "bold", "font-size": "11px", "margin-bottom": "8px", "color": "#666", "text-transform": "uppercase", "letter-spacing": "0.5px" }).text(isUpdate ? "Update Link" : "Setup Link"));

            var linkRow = $("<div>").css({ "display": "flex", "align-items": "center", "gap": "10px" });
            var linkText = $("<div>").css({
                "border": "1px solid #ddd",
                "padding": "8px 10px",
                "font-family": "monospace",
                "font-size": "13px",
                "word-break": "break-all",
                "flex": "1",
                "background-color": "#f9f9f9"
            }).text(this._options.setupLink);
            linkRow.append(linkText);

            var copyButton = new Solovis.UI.Buttons.Button({
                text: "Copy Link",
                size: "small",
                click: function () {
                    self._copyToClipboard(self._options.setupLink);
                }
            });
            linkRow.append(copyButton.getElement());
            linkBox.append(linkRow);
            body.append(linkBox);

            // Cert rotation callout — update dialog only
            if (isUpdate) {
                var certCallout = $("<div>").css({
                    "background-color": "#fcf8e3",
                    "border": "1px solid #faebcc",
                    "color": "#8a6d3b",
                    "padding": "12px 15px",
                    "border-radius": "4px",
                    "margin-bottom": "15px"
                });
                certCallout.append($("<strong>").text("Signing certificate rotated? "));
                certCallout.append(document.createTextNode(
                    "If your identity provider has rotated its signing certificate, open this link and go through the setup again. " +
                    "On the Configure Connection step, upload the new certificate. " +
                    "The connection will use the updated certificate as soon as the setup is complete."
                ));
                body.append(certCallout);
            }

            // What the link does
            body.append($("<p>").css({ "font-weight": "bold" }).text("What the link does"));
            if (isUpdate) {
                body.append($("<p>").text("It opens Auth0’s guided setup for this connection, at the configuration step. Inside it, the person can:"));
                var updateWhatList = $("<ul>").css({ "margin-bottom": "15px" });
                updateWhatList.append($("<li>").text("update the identity provider sign-in URL"));
                updateWhatList.append($("<li>").text("update the sign-out URL"));
                updateWhatList.append($("<li>").text("upload a new signing certificate or new identity provider metadata"));
                updateWhatList.append($("<li>").text("view the attribute mapping, which stays read-only and does not change"));
                body.append(updateWhatList);
            }
            else {
                body.append($("<p>").text("It opens Auth0’s guided setup for this connection. Inside it, the person:"));
                var setupWhatList = $("<ul>").css({ "margin-bottom": "15px" });
                setupWhatList.append($("<li>").text("sees the SP values to enter on the identity provider (ACS URL, SP Entity ID / metadata)"));
                setupWhatList.append($("<li>").text("provides the IdP details (sign-in URL, signing certificate, metadata)"));
                setupWhatList.append($("<li>").text("sees the attribute mapping, which is read-only; the only instruction is to send the user’s email in the named claim"));
                setupWhatList.append($("<li>").text("checks that the correct binding is set in the additional configuration inside the wizard"));
                body.append(setupWhatList);
            }

            // After the wizard/setup is completed
            body.append($("<p>").css({ "font-weight": "bold" }).text(isUpdate ? "After the setup is completed" : "After the wizard is completed"));
            if (isUpdate) {
                body.append($("<p>").css({ "margin-bottom": "15px" }).text(
                    "The connection is updated immediately. Every user on this connection’s email domains signs in using the updated configuration from that point. If the certificate or sign-in URL is wrong, users on these domains will not be able to sign in until it is corrected."
                ));
            }
            else {
                var afterParagraph = $("<p>").css({ "margin-bottom": "15px" });
                afterParagraph.append("Come back to Manage Connections, open this connection, run ");
                afterParagraph.append($("<strong>").text("Validate Connection"));
                afterParagraph.append(", complete a test sign-in through the test link, then ");
                afterParagraph.append($("<strong>").text("Enable"));
                afterParagraph.append(" the connection. The connection is tested and enabled only from here, not from the identity provider side.");
                body.append(afterParagraph);
            }

            // Security warning (yellow)
            var warningBanner = $("<div>").css({
                "background-color": "#fcf8e3",
                "border": "1px solid #faebcc",
                "color": "#8a6d3b",
                "padding": "12px 15px",
                "border-radius": "4px",
                "margin-bottom": "15px"
            });
            warningBanner.text(
                isUpdate
                    ? "This link grants access to modify the connection. Store it securely and share it only with authorized individuals."
                    : "This link grants access to create the connection. Store it securely and share it only with authorized individuals."
            );
            body.append(warningBanner);

            // Link validity — identical for setup and update; only what the link opens into differs.
            body.append($("<p>").css({ "font-weight": "bold" }).text("Link validity"));
            var validityList = $("<ul>").css({ "margin-bottom": "15px" });
            validityList.append($("<li>").text("The link stays valid for 5 days after it is generated."));
            validityList.append($("<li>").text("Once the link is first opened, the admin has 5 hours to finish."));
            validityList.append($("<li>").text("The link can be opened at most 10 times. After that, a new link has to be generated."));
            validityList.append($("<li>").text("A link can be revoked before it expires, which cuts off access to the guided setup immediately."));
            body.append(validityList);

            // Horizontal rule
            body.append($("<hr>").css({ "border": "none", "border-top": "1px solid #ddd", "margin": "15px 0" }));

            // Regenerate (also doubles as revoke — see the confirmation shown before this dialog opened)
            var regenerateParagraph = $("<p>");
            regenerateParagraph.append($("<strong>").text("To get a new link"));
            regenerateParagraph.append(
                isUpdate
                    ? " — because this one expired, hit the 10-open limit, or for any other reason — click Update Connection again. The previous link stops working the moment the new one is issued."
                    : " — because this one expired, hit the 10-open limit, or for any other reason — click Configure again. The previous link stops working the moment the new one is issued."
            );
            body.append(regenerateParagraph);

            dialog = new (Solovis as any).UI.Dialog({
                container: body,
                title: isUpdate ? "Update this connection" : "Set up this connection",
                modal: true,
                autoOpen: true,
                center: true,
                destroyOnClose: true,
                actions: ["Close"],
                width: 600,
                close: function () {
                    if (self._options.onClose) {
                        self._options.onClose();
                    }
                },
                buttons: [
                    {
                        label: "Close",
                        click: function () {
                            dialog.close();
                        }
                    }
                ]
            });
        }

        private _copyToClipboard(text: string) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text);
            } else {
                var textArea = $("<textarea>").val(text).css({ "position": "fixed", "opacity": "0" }).appendTo(document.body);
                (textArea[0] as HTMLTextAreaElement).select();
                document.execCommand("copy");
                textArea.remove();
            }
        }
    }

}
